import axios from 'axios';
import { store } from '../store/store';
import { loginSuccess, logoutSuccess, logout } from '../store/slices/authSlice';
import { getPersistedSessionId } from '../utils/auth'; // <-- Session ID எடுக்க
import {
  getStoredAccessToken,
  getStoredMeetingSessionId,
  readStoredAuth,
} from './meetingAuth';

const ORIGIN = (typeof window !== 'undefined' && window.location && window.location.origin) ? window.location.origin : '';
const DEFAULT_API = ORIGIN ? `${ORIGIN.replace(/\/$/, '')}/api` : 'https://shepherdsindia.sbs/api';
const API_BASE = (process.env.REACT_APP_API_URL || DEFAULT_API);

console.log("API_BASE:", API_BASE);

const api = axios.create({
  baseURL: API_BASE,
  timeout: 20000,
  headers: { 'Content-Type': 'application/json' },
  withCredentials: true,
});

export function getAuthToken() {
  return getStoredAccessToken();
}

export function getUserId() {
  try {
    const state = store.getState?.();
    const uid = state?.auth?.user?._id || state?.auth?.user?.id;
    if (uid) return uid;
  } catch {}
  const auth = readStoredAuth();
  return (
    sessionStorage.getItem('userId') ||
    localStorage.getItem('userId') ||
    auth?.user?._id ||
    auth?.user?.id ||
    ''
  );
}

export function setAuthToken(token, persist = false) {
  if (persist) localStorage.setItem('token', token);
  else sessionStorage.setItem('token', token);
}

export function clearAuthToken() {
  sessionStorage.removeItem('token');
  localStorage.removeItem('token');
}

export function setUserId(uid) {
  sessionStorage.setItem('userId', uid);
  localStorage.setItem('userId', uid);
}

// ==========================================
// REQUEST INTERCEPTOR (Send Token & User ID)
// ==========================================
api.interceptors.request.use((config) => {
  // Preserve file bytes: Axios otherwise serializes FormData as JSON under
  // this client's default Content-Type. Let the browser set the boundary.
  if (typeof FormData !== 'undefined' && config.data instanceof FormData) {
    if (typeof config.headers?.setContentType === 'function') config.headers.setContentType(undefined);
    else if (config.headers) {
      delete config.headers['Content-Type'];
      delete config.headers['content-type'];
    }
  }
  const state = store.getState?.();
  const token = state?.auth?.token || getStoredAccessToken();
    
  if (token) config.headers.Authorization = `Bearer ${token}`;

  const uid = state?.auth?.user?.id || state?.auth?.user?._id || getUserId();
    
  if (uid) config.headers['X-User-Id'] = uid;

  // Bind every MediaSoup REST request to the same participant session used by
  // Socket.IO. This is essential when one account has a recently-disconnected
  // session and a newly-admitted session in the same room.
  const requestUrl = String(config.url || '');
  const isMeetingChatRequest = /\/rooms\/[^/]+\/chat(?:\/|$)/.test(requestUrl);
  if (requestUrl.includes('/mediasoup/') || isMeetingChatRequest) {
    const meetingSessionId = getStoredMeetingSessionId();
    if (meetingSessionId) {
      config.headers['X-Meeting-Session-Id'] = meetingSessionId;
    }
  }

  return config;
});

const refreshFlights = new Map();

const persistRefreshedAuth = (data = {}, expectedSessionId = '') => {
  const currentSessionId = String(getStoredMeetingSessionId() || '').trim();
  const expected = String(expectedSessionId || '').trim();
  const responseSessionId = String(data.sessionId || '').trim();
  if (
    !expected ||
    currentSessionId !== expected ||
    (responseSessionId && responseSessionId !== expected)
  ) {
    const error = new Error(
      'Authentication changed while the previous session was refreshing.'
    );
    error.code = 'AUTH_REFRESH_SUPERSEDED';
    throw error;
  }

  const prev = readStoredAuth() || {};
  const merged = { ...prev, ...data, persistedAt: Date.now() };
  const serialized = JSON.stringify(merged);
  const currentSessionAuth = (() => {
    try {
      return JSON.parse(sessionStorage.getItem('auth_v1') || 'null');
    } catch {
      return null;
    }
  })();
  const isTabScoped =
    Boolean(currentSessionAuth?.token && currentSessionAuth?.user) &&
    String(currentSessionAuth?.sessionId || '') === expected;
  const targetStorage = isTabScoped ? sessionStorage : localStorage;

  // Refresh only the store that owns this tab's identity. Updating both stores
  // lets one meeting tab overwrite another tab's room-specific session.
  targetStorage.setItem('auth_v1', serialized);
  if (data.token) {
    targetStorage.setItem('accessToken', data.token);
    targetStorage.setItem('token', data.token);
  }
  const sessionId = data.sessionId || prev.sessionId || getStoredMeetingSessionId();
  const userId =
    data.user?._id ||
    data.user?.id ||
    prev.user?._id ||
    prev.user?.id ||
    getUserId();
  if (sessionId) {
    targetStorage.setItem('sessionId', String(sessionId));
    if (isTabScoped) {
      sessionStorage.setItem('meetingTabSessionId', String(sessionId));
    }
  }
  if (userId) {
    targetStorage.setItem('userId', String(userId));
  }
  store.dispatch(loginSuccess(merged));
  window.dispatchEvent(new Event('auth-changed'));
  return merged;
};

/**
 * Socket.IO can outlive a short JWT. Expose the same single-flight
 * session-validation path used by REST so a reconnect handshake can obtain a
 * fresh token before rejoining the media room.
 */
export const refreshAuthSession = async () => {
  const sessionId = String(
    getPersistedSessionId() || getStoredMeetingSessionId() || ''
  ).trim();
  if (!sessionId) {
    throw new Error('No session ID found to re-authenticate.');
  }
  if (!refreshFlights.has(sessionId)) {
    const request = axios
      .post(`${API_BASE}/auth/session-validate`, { sessionId })
      .then(({ data }) => persistRefreshedAuth(data, sessionId))
      .finally(() => {
        if (refreshFlights.get(sessionId) === request) {
          refreshFlights.delete(sessionId);
        }
      });
    refreshFlights.set(sessionId, request);
  }
  return refreshFlights.get(sessionId);
};

// ==========================================
// RESPONSE INTERCEPTOR (Handle 401 Expiry via SessionId)
// ==========================================
api.interceptors.response.use(
  (res) => res,
  async (error) => {
    // Prevent "Cannot destructure property 'url' of 'e'" crash
    if (!error.config) {
      error.config = { url: 'unknown' };
    }
    error.url = error.config.url; 

    const status = error?.response?.status;
    const original = error.config || {};
    
    // Token Expired / 401 Unauthorized வந்தால்:
    if (status === 401 && !original._retry) {
      original._retry = true;
      try {
        const merged = await refreshAuthSession();
        
        // 4. புதிய Token-ஐ வைத்து பழைய Request-ஐ மீண்டும் அனுப்புகிறோம்
        original.headers = original.headers || {};
        original.headers.Authorization = `Bearer ${merged.token}`;
        return api(original);
        
      } catch (e) {
        if (e?.code === 'AUTH_REFRESH_SUPERSEDED') {
          return Promise.reject(error);
        }
        console.error("Session Validation Failed. Logging out...", e);
        store.dispatch(logoutSuccess()); // Token புதுப்பிக்க முடியாவிட்டால் Logout செய்கிறோம்
        return Promise.reject(error);
      }
    }
    return Promise.reject(error);
  }
);

// --- Convenience API helpers ---
export const getUser = (userId) => api.get(`/users/${userId}`);
export const registerDeviceToken = (token) => api.post('/notifications/register', { token });
export const getMyCounters = () => api.get('/me/counters');
export const resetMyCounters = (payload = {}) => api.post('/me/counters/reset', payload);
export const getRoom = (roomId) => api.get(`/rooms/${roomId}`);
export const getChatMessages = (roomId) => api.get(`/rooms/${roomId}/chat`);
export const markRoomChatRead = (roomId, cutoff) =>
  api.put(`/rooms/${roomId}/chat/read`, cutoff ? { cutoff } : {});
export const getPrivateChatMessages = (roomId, counterpartUserId = '') =>
  api.get(`/rooms/${roomId}/chat/private`, {
    params: counterpartUserId ? { counterpartUserId } : {},
  });
export const markPrivateChatRead = (roomId, counterpartUserId = '', cutoff) =>
  api.put(`/rooms/${roomId}/chat/private/read`, {
    ...(counterpartUserId ? { counterpartUserId } : {}),
    ...(cutoff ? { cutoff } : {}),
  });
export const getRoomProducers = (
  roomId,
  sessionId = getStoredMeetingSessionId()
) =>
  api.get(`/mediasoup/${encodeURIComponent(roomId)}/producers`, {
    params: {
      ...(sessionId ? { sessionId } : {}),
      _ts: Date.now(),
    },
  });
export const simulateChat = (body) => api.post('/events/chat', body);
export const simulateWaiting = (body) => api.post('/events/waiting', body);

export default api;
